import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/api';

const IMG_BASE = process.env.REACT_APP_IMG_URL || 'http://localhost:5000';

const CATEGORIES = [
  { value: 'udheheqja',    label: '👔 Udhëheqja' },
  { value: 'departamenti', label: '🏥 Departamenti' },
  { value: 'sherbimi',     label: '⚕️ Shërbimi' },
];

export default function AdminLeadershipForm() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const isEdit   = Boolean(id);

  const [form, setForm] = useState({
    name: '', position: '', category: 'udheheqja',
    department_id: '', service_id: '',
    email: '', phone: '', bio: '', sort_order: 0, is_active: true,
  });
  const [departments, setDepartments] = useState([]);
  const [services,    setServices]    = useState([]);
  const [photoFile,   setPhotoFile]   = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/admin/departments').catch(() => ({ data: [] })),
      api.get('/admin/services').catch(() => ({ data: [] })),
    ]).then(([dRes, sRes]) => {
      setDepartments(dRes.data);
      setServices(sRes.data);
    });
  }, []);

  useEffect(() => {
    if (!isEdit) { setLoading(false); return; }
    api.get('/admin/leadership')
      .then(res => {
        const leader = res.data.find(item => String(item.id) === id);
        if (!leader) { toast.error('Nuk u gjet.'); return; }
        setForm({
          name:          leader.name          || '',
          position:      leader.position      || '',
          category:      leader.category      || 'udheheqja',
          department_id: leader.department_id || '',
          service_id:    leader.service_id    || '',
          email:         leader.email         || '',
          phone:         leader.phone         || '',
          bio:           leader.bio           || '',
          sort_order:    leader.sort_order    || 0,
          is_active:     leader.is_active === 1 || leader.is_active === true,
        });
        if (leader.photo) setPhotoPreview(`${IMG_BASE}${leader.photo}`);
      })
      .catch(() => toast.error('Gabim gjatë ngarkimit.'))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.position.trim())
      return toast.error('Emri dhe pozicioni janë të detyrueshëm.');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('name',          form.name);
      fd.append('position',      form.position);
      fd.append('category',      form.category      || 'udheheqja');
      fd.append('department_id', form.department_id || '');
      fd.append('service_id',    form.service_id    || '');
      fd.append('email',         form.email         || '');
      fd.append('phone',         form.phone         || '');
      fd.append('bio',           form.bio           || '');
      fd.append('sort_order',    form.sort_order    || 0);
      fd.append('is_active',     form.is_active ? '1' : '0'); // explicit
      if (photoFile) fd.append('photo', photoFile);

      if (isEdit) {
        await api.put(`/admin/leadership/${id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('U përditësua me sukses.');
      } else {
        await api.post('/admin/leadership', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        toast.success('U krijua me sukses.');
      }
      navigate('/admin/udheheqja');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gabim.');
    } finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><div className="spinner"/></AdminLayout>;

  const cat = form.category;

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="page-title">{isEdit ? 'Ndrysho Punonjësin' : 'Punonjës i Ri'}</h1>
        <Link to="/admin/udheheqja" className="btn btn-secondary">← Kthehu</Link>
      </div>

      <form onSubmit={handleSubmit} style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:'1.25rem',alignItems:'start'}}>
        <div className="card" style={{padding:'1.5rem'}}>
          {/* Category selector */}
          <div className="form-group">
            <label className="form-label">Kategoria *</label>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {CATEGORIES.map(c => (
                <label key={c.value} style={{
                  display:'flex', alignItems:'center', gap:6,
                  padding:'8px 16px', borderRadius:8, cursor:'pointer',
                  border: form.category === c.value ? '2px solid var(--blue-700)' : '2px solid #dddbd6',
                  background: form.category === c.value ? 'var(--blue-50)' : '#fff',
                  fontWeight: form.category === c.value ? 700 : 400,
                  fontSize:'0.875rem',
                }}>
                  <input type="radio" name="category" value={c.value}
                    checked={form.category === c.value}
                    onChange={e => setForm(p => ({...p, category: e.target.value, department_id: '', service_id: ''}))}
                    style={{display:'none'}}/>
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          {cat === 'departamenti' && (
            <div className="form-group">
              <label className="form-label">Departamenti</label>
              <select className="form-input form-select" value={form.department_id}
                onChange={e => setForm(p => ({...p, department_id: e.target.value}))}>
                <option value="">— Zgjidh departamentin —</option>
                {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
          )}

          {cat === 'sherbimi' && (
            <div className="form-group">
              <label className="form-label">Shërbimi</label>
              <select className="form-input form-select" value={form.service_id}
                onChange={e => setForm(p => ({...p, service_id: e.target.value}))}>
                <option value="">— Zgjidh shërbimin —</option>
                {services.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          )}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem'}}>
            <div className="form-group">
              <label className="form-label">Emri dhe Mbiemri *</label>
              <input className="form-input" required value={form.name}
                onChange={e => setForm(p => ({...p, name: e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Pozicioni / Titulli *</label>
              <input className="form-input" required placeholder="p.sh. Drejtori i Spitalit"
                value={form.position}
                onChange={e => setForm(p => ({...p, position: e.target.value}))}/>
            </div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'1rem'}}>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email}
                onChange={e => setForm(p => ({...p, email: e.target.value}))}/>
            </div>
            <div className="form-group">
              <label className="form-label">Telefon</label>
              <input className="form-input" value={form.phone}
                onChange={e => setForm(p => ({...p, phone: e.target.value}))}/>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Bio / Prezantimi</label>
            <textarea className="form-input" rows={4} value={form.bio}
              onChange={e => setForm(p => ({...p, bio: e.target.value}))}/>
          </div>
        </div>

        <div>
          <div className="card" style={{padding:'1.25rem', marginBottom:'1rem'}}>
            <h3 className="form-side-title">Foto</h3>
            {photoPreview && (
              <img src={photoPreview} alt="" style={{
                width:'100%', height:180, objectFit:'cover', objectPosition:'top',
                borderRadius:8, marginBottom:10,
              }}/>
            )}
            <input type="file" accept="image/*" className="form-input"
              onChange={e => {
                const f = e.target.files[0];
                setPhotoFile(f);
                if (f) setPhotoPreview(URL.createObjectURL(f));
              }}/>
          </div>

          <div className="card" style={{padding:'1.25rem', marginBottom:'1rem'}}>
            <h3 className="form-side-title">Renditja</h3>
            <input className="form-input" type="number" min="0" value={form.sort_order}
              onChange={e => setForm(p => ({...p, sort_order: Number(e.target.value)}))}/>
          </div>

          <div className="card" style={{padding:'1.25rem', marginBottom:'1rem'}}>
            <h3 className="form-side-title">Statusi</h3>
            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:'0.875rem',fontWeight:500}}>
              <input type="checkbox"
                checked={form.is_active}
                onChange={e => setForm(p => ({...p, is_active: e.target.checked}))}/>
              Aktiv (i dukshëm)
            </label>
          </div>

          <button type="submit" className="btn btn-primary"
            style={{width:'100%', justifyContent:'center'}} disabled={saving}>
            {saving ? 'Duke ruajtur...' : isEdit ? 'Ruaj ndryshimet' : 'Krijo punonjësin'}
          </button>
        </div>
      </form>
    </AdminLayout>
  );
}
